import { ReactNode } from 'react';
import { MenuButtonProps } from '@nimbus-ds/menubutton';
import { PromotionType } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { DiscountCircleIcon, PlusCircleIcon } from '@tiendanube/icons';
import { useNavegate } from 'App/hooks';
import {
  ActionProp,
  ActionsDesktop,
  ErrorScreen,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  PopoverMenu,
} from 'commons/components';
import { MoreDiscounts } from 'domains/Marketing/components/MoreDiscounts';
import {
  useMoreDiscountsModal,
  useTranslationMarketing,
} from 'domains/Marketing/hook';
import { NewPromoAlert } from './NewPromoAlert';
import { PromotionAlertOpenFTP } from './PromotionAlertOpenFTP';
import { PromotionHelpLink } from './PromotionHelpLink';
import { PromotionsListEmpty } from './PromotionsListEmpty';
import { PROMOTIONS_ROUTES } from '../promotionsRoutes';
import {
  promotionsCreationView,
  promotionsOpenAdoptionAlert,
  promotionsCloseAdoptionAlert,
  promotionsShowAdoptionAlert,
} from '../tracking';
import { buildNewPromotionUrl } from '../utils';

interface PromotionsListPageBaseProps {
  skeleton: ReactNode;
  isLoading: boolean;
  isEmpty: boolean;
  isError: boolean;
  children: ReactNode;
  onRetry: () => void;
}

export function PromotionsListPageBase({
  skeleton,
  isLoading,
  isEmpty,
  isError,
  children,
  onRetry,
}: Readonly<PromotionsListPageBaseProps>) {
  const t = useTranslationMarketing('promotions');
  const tMoreDiscounts = useTranslationMarketing('moreDiscounts');
  const { goTo } = useNavegate();

  const { moreDiscountsIsOpen, openMoreDiscounts, closeMoreDiscounts } =
    useMoreDiscountsModal('promotions');

  const gotoCreate = (promotionType?: PromotionType) => {
    if (promotionType) {
      promotionsOpenAdoptionAlert(promotionType);
      goTo(buildNewPromotionUrl({ promotionType }));
    } else {
      promotionsCreationView();
      goTo(PROMOTIONS_ROUTES.newPromotions);
    }
  };

  const createPromotionAction: ActionProp = {
    icon: PlusCircleIcon,
    children: t('add'),
    appearance: 'default',
    onClick: gotoCreate,
  };
  const mobileMenuActions: MenuButtonProps[] = [
    {
      startIcon: DiscountCircleIcon,
      label: tMoreDiscounts('ctas.open'),
      onClick: openMoreDiscounts,
    },
  ];
  const desktopActions: ActionProp[] = [
    {
      icon: DiscountCircleIcon,
      children: tMoreDiscounts('ctas.open'),
      appearance: 'default',
      onClick: openMoreDiscounts,
    },
    {
      icon: PlusCircleIcon,
      children: t('addPromotion'),
      appearance: 'primary',
      onClick: () => gotoCreate(),
    },
  ];

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop
          mainAction={!isEmpty ? createPromotionAction : undefined}
          actions={
            <PopoverMenu
              buttonColor="transparent"
              actions={mobileMenuActions}
            />
          }
        />
      }
      headerContent={
        !isEmpty ? (
          <HeaderContent
            title={t('title')}
            actions={<ActionsDesktop actions={desktopActions} />}
          />
        ) : undefined
      }
      headerFixed
    >
      {isLoading && skeleton}
      {isError && <ErrorScreen onRetry={onRetry} />}
      {isEmpty && <PromotionsListEmpty onCreate={() => gotoCreate()} />}
      {!isLoading && !isError && !isEmpty && (
        <>
          <NewPromoAlert
            onClick={gotoCreate}
            onRemove={promotionsCloseAdoptionAlert}
            onShow={promotionsShowAdoptionAlert}
          />
          <PromotionAlertOpenFTP />
          {children}
        </>
      )}
      {!isError && <PromotionHelpLink />}

      <MoreDiscounts
        isOpen={moreDiscountsIsOpen}
        onClose={closeMoreDiscounts}
      />
    </IonPageStratus>
  );
}
